import { useWallet } from '@solana/wallet-adapter-react';
import { DollarSign, Info } from 'lucide-react';
import React, { use } from 'react';
import { TRPCContext } from '../../../contexts/TRPC';
import { cn } from '../../../lib/utils';
import { Button } from '../../Button';
import * as Form from '../../Form';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import type { ProviderConfigSectionProps } from './types';
import { useArDriveBalance } from './useArDriveBalance';
import { useArDrivePendingRecovery } from './useArDrivePendingRecovery';
import { useArDriveTopUp } from './useArDriveTopUp';

const formatArValue = (value: string | null, arToUsdRate: number | null) => {
  const parsed = Number.parseFloat(value ?? '0');
  const current = Number.isFinite(parsed) ? parsed : 0;
  let usdText = '';
  if (arToUsdRate && arToUsdRate > 0 && current > 0) {
    const usdValue = current * arToUsdRate;
    const formattedUsd =
      usdValue < 0.01 ? usdValue.toFixed(4) : usdValue.toFixed(2);
    usdText = ` ($${formattedUsd})`;
  }

  return `${current.toFixed(4)} AR${usdText}`;
};

function EstimateRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-seeker-400">{label}</span>
      <span className={cn('font-mono text-white tabular-nums', valueClassName)}>
        {value}
      </span>
    </div>
  );
}

export const ArDriveConfigSection: React.FC<ProviderConfigSectionProps> = ({
  isSelected,
}) => {
  const wallet = useWallet();
  const trpc = use(TRPCContext);
  const {
    ardriveBalance,
    fileSize,
    setFileSize,
    estimatedCost,
    remainingArDriveBalance,
    arToUsdRate,
    setArToUsdRate,
    checkArDriveBalance,
  } = useArDriveBalance({ isSelected, wallet });
  useArDrivePendingRecovery({
    isSelected,
    wallet,
    trpc,
    checkArDriveBalance,
  });
  const {
    showArDriveTopUpModal,
    setShowArDriveTopUpModal,
    topUpAmount,
    setTopUpAmount,
    isToppingUp,
    solToUsdRate,
    handleArDriveTopUp,
  } = useArDriveTopUp({
    wallet,
    trpc,
    arToUsdRate,
    setArToUsdRate,
    checkArDriveBalance,
  });
  const topUpSol = parseFloat(topUpAmount) || 0;
  const remainingIsNegative =
    parseFloat(remainingArDriveBalance || ardriveBalance || '0') < 0;

  return (
    <>
      <div className="mt-4 border-t border-seeker-700 pt-4">
        <div className="flex flex-col gap-4 rounded-lg bg-seeker-900 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-medium text-white">
              <DollarSign className="size-4 shrink-0" aria-hidden="true" />
              <span>Cost Estimator: {fileSize} MB</span>
            </div>
            <Button
              variant="outlineMuted"
              className="h-8 px-3"
              onClick={() => setShowArDriveTopUpModal(true)}
            >
              <span className="text-xs">Top Up Balance</span>
            </Button>
          </div>
          <div>
            <input
              type="range"
              aria-label="File size in megabytes"
              min={1}
              max={500}
              step={1}
              value={fileSize}
              onChange={(event) =>
                setFileSize(event.currentTarget.valueAsNumber)
              }
              className="w-full accent-white"
            />
            <div className="mt-1 flex justify-between text-xs leading-4 text-seeker-400">
              <span>1 MB</span>
              <span>500 MB</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs leading-4">
            <div className="text-center">
              <p className="text-seeker-400">Current</p>
              <span className="flex h-4 items-center justify-center font-mono text-green-400 tabular-nums">
                {formatArValue(ardriveBalance, arToUsdRate)}
              </span>
            </div>
            <div className="text-center">
              <p className="text-seeker-400">Cost</p>
              <span className="flex h-4 items-center justify-center font-mono text-amber-400 tabular-nums">
                {formatArValue(estimatedCost, arToUsdRate)}
              </span>
            </div>
            <div className="text-center">
              <p className="text-seeker-400">Remaining</p>
              <span
                className={cn(
                  'flex h-4 items-center justify-center font-mono tabular-nums',
                  remainingIsNegative ? 'text-rose-400' : 'text-green-400',
                )}
              >
                {formatArValue(
                  remainingArDriveBalance || ardriveBalance,
                  arToUsdRate,
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={showArDriveTopUpModal}
        onOpenChange={setShowArDriveTopUpModal}
      >
        <DialogContent className="max-w-md rounded-xl border-seeker-800 bg-seeker-900 p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">
              Top Up ArDrive Balance
            </DialogTitle>
            <DialogDescription className="text-sm text-seeker-400">
              Purchase ArDrive Turbo credits with SOL. The transaction will be
              signed with your connected wallet and sent to the Turbo service.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-6">
            <div className="flex gap-2 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3 text-xs leading-4">
              <Info
                className="mt-0.5 size-4 shrink-0 text-blue-400"
                aria-hidden="true"
              />
              <div className="space-y-1 text-seeker-300">
                <p className="font-medium text-blue-400">About Turbo Credits</p>
                <p>
                  Turbo credits are not blockchain tokens and cannot be
                  transferred, traded, or sold. They are non-refundable storage
                  credits specific to your wallet address.
                </p>
              </div>
            </div>

            <Form.Input
              label="Amount (SOL)"
              name="topUpAmount"
              type="number"
              aria-label="Amount (SOL)"
              value={topUpAmount}
              onChange={(e) => {
                setTopUpAmount(e.target.value);
              }}
              min="0.005"
              max="1"
              step="0.01"
              placeholder="0.01"
              explanation="Minimum: 0.005 SOL, Maximum: 1 SOL"
            />

            <div className="flex flex-col gap-3 rounded-lg bg-seeker-800 p-4">
              <h3 className="text-sm font-medium text-white">
                Estimated Value
              </h3>

              <div className="flex flex-col gap-2">
                <EstimateRow
                  label="SOL Amount"
                  value={`${topUpSol.toFixed(2)} SOL`}
                />
                <EstimateRow
                  label="Current AR Rate"
                  value={
                    arToUsdRate
                      ? `1 AR = $${arToUsdRate.toFixed(2)} USD`
                      : 'Loading...'
                  }
                  valueClassName="text-seeker-300"
                />
                <EstimateRow
                  label="Estimated Value"
                  value={
                    solToUsdRate
                      ? `$${(topUpSol * solToUsdRate).toFixed(2)} USD`
                      : 'Loading...'
                  }
                  valueClassName="text-green-400"
                />

                {solToUsdRate && arToUsdRate && (
                  <>
                    <EstimateRow
                      label="Estimated AR"
                      value={`~${formatArValue(
                        ((topUpSol * solToUsdRate) / arToUsdRate).toFixed(4),
                        null,
                      )}`}
                      valueClassName="text-green-400"
                    />

                    {estimatedCost && (
                      <EstimateRow
                        label="Estimated Storage"
                        value={`~${(
                          ((topUpSol * solToUsdRate) /
                            arToUsdRate /
                            parseFloat(estimatedCost)) *
                          fileSize
                        ).toFixed(0)}MB`}
                        valueClassName="text-blue-400"
                      />
                    )}
                  </>
                )}
              </div>

              <p className="border-t border-seeker-700 pt-2 text-xs leading-4 text-seeker-400">
                {solToUsdRate ? (
                  <>
                    {`Current SOL price: $${solToUsdRate.toFixed(2)} USD. `}
                    The exact AR amount depends on exchange rates when
                    processed.
                  </>
                ) : (
                  'Loading current SOL price...'
                )}
              </p>
            </div>
          </DialogBody>

          <DialogFooter className="gap-2">
            <Button
              variant="outlineMuted"
              className="w-full sm:w-[120px]"
              onClick={() => setShowArDriveTopUpModal(false)}
              disabled={isToppingUp}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-full sm:min-w-[120px] sm:w-auto"
              onClick={handleArDriveTopUp}
              disabled={isToppingUp}
              submitting={isToppingUp}
            >
              Top Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
